import { safeRedrawAll } from './chartLifecycle.js'

// Single source of truth for cross-chart drill-down in FlowSight Conversions.
// Chart factories only translate clicks into source values and ask this
// controller how those values should look. Filtering, reset, feedback
// notification and redraw scheduling all live here.
//
// Each source owns its OWN crossfilter dimension (passed via registerSource's
// `dimension` option) and this controller only ever filters THAT source's own
// values onto THAT source's own dimension - it never combines multiple
// sources' selections into one manual intersection. Crossfilter already does
// that combination for free across separate dimension instances; the only
// reason to hand-roll it (as this used to) is if multiple sources share one
// dimension, but sharing is exactly what breaks self-exclusion - see
// [[flowsight-drilldown-controller]].
export function createDrilldownController ({ chartGroup, onChange }) {
  const sources = new Map()
  let redrawQueued = false

  const requestRedraw = () => {
    if (redrawQueued) return
    redrawQueued = true
    requestAnimationFrame(() => {
      redrawQueued = false
      safeRedrawAll(chartGroup)
    })
  }

  const source = (id) => {
    const entry = sources.get(id)
    if (!entry) throw new Error(`Unknown drill-down source: ${id}`)
    return entry
  }

  const selectedTenorSets = () => Array.from(sources.values())
    .filter((entry) => entry.values.size > 0)
    .map((entry) => new Set(Array.from(entry.values, entry.toTenorIndex).filter(Number.isFinite)))
    .filter((set) => set.size > 0)

  const selectedTenorIdxs = () => {
    const sets = selectedTenorSets()
    if (!sets.length) return []
    const [first, ...rest] = sets
    return Array.from(first).filter((idx) => rest.every((set) => set.has(idx)))
  }

  const applyFilter = () => {
    sources.forEach((entry) => {
      if (!entry.dimension) return // e.g. heatmap: filters its own dimension natively, via dc
      entry.dimension.filterAll()
      if (entry.values.size > 0) {
        entry.dimension.filterFunction((key) => entry.filterPredicate
          ? entry.filterPredicate(key, entry.values)
          : entry.values.has(key))
      }
    })
  }

  const notify = ({ redraw = true } = {}) => {
    applyFilter()
    onChange?.({ active: hasSelections(), selectedTenorIdxs: selectedTenorIdxs() })
    if (redraw) requestRedraw()
  }

  const registerSource = (id, options = {}) => {
    sources.set(id, {
      values: new Set(),
      toTenorIndex: options.toTenorIndex || ((value) => Number(value)),
      toDimensionKey: options.toDimensionKey || null,
      // The crossfilter dimension THIS source's own clicks should filter -
      // must be the same instance the source's own chart renders from, so
      // the source's own selection self-excludes there but genuinely
      // affects every other chart's (differently-dimensioned) groups.
      // Omit for sources that already filter their own dimension natively
      // through dc (e.g. heatmap's default filterHandler).
      dimension: options.dimension || null,
      filterPredicate: options.filterPredicate || null,
    })
    return api
  }

  const replace = (id, values, options = {}) => {
    const entry = source(id)
    entry.values.clear()
    for (const value of values || []) entry.values.add(value)
    for (const otherId of options.clear || []) source(otherId).values.clear()
    notify(options)
  }

  const toggle = (id, value, options = {}) => {
    const entry = source(id)
    if (entry.values.has(value)) entry.values.delete(value)
    else entry.values.add(value)
    for (const otherId of options.clear || []) source(otherId).values.clear()
    notify(options)
  }

  const clear = (id, options = {}) => {
    source(id).values.clear()
    notify(options)
  }

  const clearAll = (options = {}) => {
    sources.forEach((entry) => entry.values.clear())
    notify(options)
  }

  const values = (id) => Array.from(source(id).values)
  const hasSelections = () => Array.from(sources.values()).some((entry) => entry.values.size > 0)
  const isSelected = (id, value) => source(id).values.has(value)
  const matchesDimensionKey = (dimensionKey) => {
    if (!hasSelections()) return true
    const tenorIdx = Number.parseInt(String(dimensionKey).split('|')[0], 10)
    const tenorIdxs = selectedTenorIdxs()
    if (tenorIdxs.length && !tenorIdxs.includes(tenorIdx)) return false
    return Array.from(sources.values())
      .filter((entry) => entry.values.size > 0 && entry.toDimensionKey)
      .every((entry) => new Set(Array.from(entry.values, entry.toDimensionKey)).has(String(dimensionKey)))
  }

  // Every chart shape binds its clickable marks to a different datum shape
  // (stacked bar/area charts wrap the real row in `d.data`, plain
  // row/bubble/heatmap charts don't) - keyFn is how each chart factory tells
  // us how to get back to the real dimension key for that mark. Getting this
  // wrong (e.g. reading `d.key` off a stack point, which is `undefined`)
  // makes every mark fail matchesDimensionKey and grey out, selected one
  // included - see the conversion-rate chart's original bug.
  const applyTransition = (selection, properties = ['fill', 'opacity']) =>
    selection
      .style('cursor', 'pointer')
      .style('transition', properties.map((p) => api.transition(p)).join(', '))

  const applySelectionClasses = (selection, keyFn) => {
    if (!hasSelections()) {
      return selection.classed('selected', false).classed('deselected', false)
    }
    return selection
      .classed('selected', (d) => matchesDimensionKey(String(keyFn(d))))
      .classed('deselected', (d) => !matchesDimensionKey(String(keyFn(d))))
  }

  // Convenience for the common case (bar/bubble charts): wire up both the
  // transition and the selected/deselected classes in one call. Charts that
  // manage their own classing (e.g. RowChart's native hasFilterHandler-driven
  // fadeDeselectedArea) should call applyTransition alone instead.
  const decorate = (selection, keyFn, properties) => {
    applyTransition(selection, properties)
    return applySelectionClasses(selection, keyFn)
  }

  const api = {
    registerSource,
    replace,
    toggle,
    clear,
    clearAll,
    values,
    hasSelections,
    isSelected,
    matchesDimensionKey,
    selectedTenorIdxs,
    transition: (property) => `${property} 300ms ease`,
    applyTransition,
    applySelectionClasses,
    decorate,
    refresh: notify,
  }
  return api
}
